import { useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { STOCK_INDEXES } from '../Constants';
import './Top5DashboardIndexV3View.scss';

const INDEX_OPTIONS = [
    [STOCK_INDEXES.SP500_INDEX, 'S&P 500'],
    [STOCK_INDEXES.SP1500_INDEX, 'S&P 1500'],
    [STOCK_INDEXES.SP400_INDEX, 'S&P MidCap 400'],
    [STOCK_INDEXES.SP600_INDEX, 'S&P SmallCap 600'],
    [STOCK_INDEXES.RUSSELL3000_INDEX, 'Russell 3000'],
    [STOCK_INDEXES.NAREIT_INDEX, 'Nareit'],
];

const ELEMENTS = ['Salary', 'Target Bonus', 'Target Total Cash', 'Annual LTI', 'Target TDC'];

const Icon = ({ type }) => {
    const paths = {
        companies: <><path d="M4 20V5l8-3 8 3v15" /><path d="M8 8h2m4 0h2M8 12h2m4 0h2M8 16h2m4 0h2M2 20h20" /></>,
        transition: <><path d="M4 7h12m0 0-3-3m3 3-3 3M20 17H8m0 0 3-3m-3 3 3 3" /></>,
        award: <><path d="M4 10h16v10H4zM3 6h18v4H3zM12 6v14" /><path d="M12 6c-1-4-6-4-6-1 0 2 3 2 6 1Zm0 0c1-4 6-4 6-1 0 2-3 2-6 1Z" /></>,
        download: <><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16" /></>,
        book: <><path d="M4 4h6a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H4zM20 4h-6a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h6z" /></>,
    };
    return <svg className="index-v3-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[type]}</svg>;
};

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const formatCurrency = (value) => {
    if (!isNumber(value)) return 'N/A';
    const absolute = Math.abs(value);
    if (absolute >= 1_000_000_000) return `${value < 0 ? '-' : ''}$${(absolute / 1_000_000_000).toFixed(2)}B`;
    if (absolute >= 1_000_000) return `${value < 0 ? '-' : ''}$${(absolute / 1_000_000).toFixed(2)}M`;
    if (absolute >= 1_000) return `${value < 0 ? '-' : ''}$${Math.round(absolute / 1_000)}K`;
    return `${value < 0 ? '-' : ''}$${Math.round(absolute).toLocaleString('en-US')}`;
};
const formatPercent = (value, digits = 1) => isNumber(value) ? `${(value * 100).toFixed(digits)}%` : 'N/A';
const formatSignedCurrency = (value) => isNumber(value) && value > 0 ? `+${formatCurrency(value)}` : formatCurrency(value);

const StatCard = ({ icon, label, value, detail }) => (
    <div className="index-v3-stat-card">
        {icon && <span className="index-v3-stat-icon"><Icon type={icon} /></span>}
        <div>
            <div className="index-v3-eyebrow">{label}</div>
            <div className="index-v3-stat-value">{value}</div>
            {detail && <div className="index-v3-stat-detail">{detail}</div>}
        </div>
    </div>
);

const ChangeValue = ({ percent, dollars }) => (
    <div className="index-v3-change-value">
        <span>{formatPercent(percent)}</span>
        <i aria-hidden="true" />
        <span>{formatSignedCurrency(dollars)}</span>
    </div>
);

const PayMixDonut = ({ payMix }) => {
    const segments = [
        { label: 'Salary', value: payMix.salary, color: '#004b76' },
        { label: 'Bonus', value: payMix.bonus, color: '#2b8bb9' },
        { label: 'LTI', value: payMix.lti, color: '#91bfd5' },
    ].filter((segment) => isNumber(segment.value) && segment.value > 0);
    if (!segments.length) return <div className="index-v3-pay-mix-empty">No pay mix data available</div>;

    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    const centerX = 130;
    const centerY = 95;
    const radius = 61;
    const labelRadius = 88;
    const circumference = 2 * Math.PI * radius;
    let cumulativeShare = 0;
    const plottedSegments = segments.map((segment) => {
        const share = segment.value / total;
        const startShare = cumulativeShare;
        cumulativeShare += share;
        const middleAngle = -Math.PI / 2 + (startShare + share / 2) * Math.PI * 2;
        const percentageX = centerX + Math.cos(middleAngle) * radius;
        const percentageY = centerY + Math.sin(middleAngle) * radius;
        const labelX = centerX + Math.cos(middleAngle) * labelRadius;
        const labelY = centerY + Math.sin(middleAngle) * labelRadius;
        const horizontalDirection = Math.cos(middleAngle);
        return {
            ...segment,
            share,
            offset: startShare,
            percentageX,
            percentageY,
            labelX,
            labelY,
            labelAnchor: Math.abs(horizontalDirection) < 0.2 ? 'middle' : horizontalDirection > 0 ? 'start' : 'end',
        };
    });

    return (
        <svg className="index-v3-donut" viewBox="0 0 260 190" role="img" aria-label="Median pay mix">
            <circle className="index-v3-donut-track" cx={centerX} cy={centerY} r={radius} />
            {plottedSegments.map((segment) => (
                <g key={segment.label}>
                    <circle
                        className="index-v3-donut-segment"
                        cx={centerX}
                        cy={centerY}
                        r={radius}
                        stroke={segment.color}
                        strokeDasharray={`${segment.share * circumference} ${circumference}`}
                        strokeDashoffset={-segment.offset * circumference}
                        transform={`rotate(-90 ${centerX} ${centerY})`}
                    />
                    <text x={segment.percentageX} y={segment.percentageY} className="index-v3-donut-percent">{formatPercent(segment.value, 0)}</text>
                    <text x={segment.labelX} y={segment.labelY} textAnchor={segment.labelAnchor} className="index-v3-donut-label">{segment.label}</text>
                </g>
            ))}
        </svg>
    );
};

const DistributionChart = ({ distribution }) => {
    const values = Object.values(distribution).filter(isNumber);
    if (!values.length) return <div className="index-v3-empty">No distribution data are available for this selection.</div>;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const x = (value) => 70 + ((value - min) / range) * 760;
    const points = [
        ['P10', distribution.p10],
        ['P25', distribution.p25],
        ['Median', distribution.median],
        ['P75', distribution.p75],
        ['P90', distribution.p90],
    ];
    return (
        <svg className="index-v3-distribution" viewBox="0 0 900 150" role="img" aria-label="Compensation percentile distribution">
            <line x1={x(distribution.p10)} y1="72" x2={x(distribution.p90)} y2="72" className="distribution-whisker" />
            <line x1={x(distribution.p10)} y1="58" x2={x(distribution.p10)} y2="86" className="distribution-cap" />
            <line x1={x(distribution.p90)} y1="58" x2={x(distribution.p90)} y2="86" className="distribution-cap" />
            <rect x={x(distribution.p25)} y="52" width={Math.max(x(distribution.p75) - x(distribution.p25), 2)} height="40" className="distribution-box" />
            <line x1={x(distribution.median)} y1="48" x2={x(distribution.median)} y2="96" className="distribution-median" />
            {points.map(([label, value], index) => (
                <g key={label}>
                    <circle cx={x(value)} cy="72" r={label === 'Median' ? 6 : 4} className={label === 'Median' ? 'distribution-dot median' : 'distribution-dot'} />
                    <text x={x(value)} y={index % 2 === 0 ? 22 : 132} textAnchor="middle" className="distribution-label">{label}</text>
                    <text x={x(value)} y={index % 2 === 0 ? 36 : 146} textAnchor="middle" className="distribution-value">{formatCurrency(value)}</text>
                </g>
            ))}
        </svg>
    );
};

const TrendChart = ({ points }) => {
    const valid = points.filter((point) => isNumber(point.value));
    if (valid.length < 2) return <div className="index-v3-empty">Three-year trend data will appear when at least two disclosure periods are available.</div>;
    const values = valid.map((point) => point.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.25 || Math.max(max * 0.1, 1);
    const y = (value) => 155 - ((value - (min - padding)) / ((max + padding) - (min - padding))) * 115;
    const x = (index) => 100 + index * (700 / Math.max(valid.length - 1, 1));
    const path = valid.map((point, index) => `${index ? 'L' : 'M'} ${x(index)} ${y(point.value)}`).join(' ');
    return (
        <svg className="index-v3-trend" viewBox="0 0 900 215" role="img" aria-label="Three-year median compensation trend">
            {[55, 100, 145].map((lineY) => <line key={lineY} x1="65" y1={lineY} x2="835" y2={lineY} className="trend-grid" />)}
            <path d={path} className="trend-line" />
            {valid.map((point, index) => (
                <g key={point.period}>
                    {index > 0 && (
                        <text x={(x(index - 1) + x(index)) / 2} y={(y(valid[index - 1].value) + y(point.value)) / 2 - 13} textAnchor="middle" className="trend-yoy">
                            {formatPercent(point.yoy, 1)} YoY
                        </text>
                    )}
                    <circle cx={x(index)} cy={y(point.value)} r="6" className="trend-dot" />
                    <rect x={x(index) - 42} y={y(point.value) - 34} width="84" height="23" rx="11" className="trend-value-pill" />
                    <text x={x(index)} y={y(point.value) - 18} textAnchor="middle" className="trend-value">{formatCurrency(point.value)}</text>
                    <text x={x(index)} y="194" textAnchor="middle" className="trend-year">{point.period}</text>
                </g>
            ))}
        </svg>
    );
};

const Top5DashboardIndexV3View = ({
    indexSummary,
    loading,
    error,
    errorDisplayStatus,
    index,
    setIndex,
    roleCode,
    setRoleCode,
    benchmarks,
    onDownload,
    onMethodology,
}) => {
    const [sector, setSector] = useState('All Sectors');
    const [element, setElement] = useState('Target TDC');
    const [showAwardTypes, setShowAwardTypes] = useState(false);
    const sectors = indexSummary?.sectors ?? [];
    const selectedSummary = useMemo(
        () => (indexSummary?.sectorSummaries ?? []).find((summary) => summary.sector === sector) ?? null,
        [indexSummary, sector],
    );
    const hasData = Boolean(indexSummary?.sectorSummaries?.length);
    const payLevels = ELEMENTS.map((label) => ({ label, ...(selectedSummary?.payLevels?.[label] ?? {}) }));
    const payMix = selectedSummary?.payMix ?? {};
    const elementStats = selectedSummary?.elements?.[element] ?? { distribution: {} };
    const trend = elementStats.trend ?? [];
    const transitions = elementStats.transitions ?? [];
    const companyCount = selectedSummary?.companyCount ?? 0;
    const transitionCount = selectedSummary?.transitionCount ?? 0;
    const specialCompanyCount = selectedSummary?.specialCompanyCount ?? 0;
    const indexLabel = INDEX_OPTIONS.find(([value]) => value === index)?.[1] ?? index;

    return (
        <div className="top5-index-v3">
            <main className="index-v3-main">
                <header className="index-v3-header">
                    <div>
                        <div className="index-v3-kicker">EXECUTIVE COMPENSATION INTELLIGENCE</div>
                        <h1>Executive Pay Index Overview</h1>
                        <p>Insights into {benchmarks[roleCode] ?? roleCode} compensation among {indexLabel} companies.</p>
                    </div>
                    <div className="index-v3-asof"><span>DATA COVERAGE</span>Reflects the most recent proxy disclosure for each {indexLabel} company with Fiscal Years ending after June 15th 2025.</div>
                </header>
                <section className="index-v3-filter-bar" aria-label="Dashboard filters">
                    <label><span>Index</span><select value={index} onChange={(event) => { setIndex(event.target.value); setSector('All Sectors'); }} disabled={loading}>{INDEX_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                    <label><span>Sector</span><select value={sector} onChange={(event) => setSector(event.target.value)} disabled={loading}><option>All Sectors</option>{sectors.map((value) => <option key={value}>{value}</option>)}</select></label>
                    <label><span>Position</span><select value={roleCode} onChange={(event) => setRoleCode(event.target.value)} disabled={loading}>{Object.entries(benchmarks).map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
                    <div className="index-v3-actions">
                        <button type="button" onClick={onDownload} disabled={!hasData || loading}><Icon type="download" />Download Data</button>
                        <button type="button" onClick={onMethodology} disabled={!hasData || loading}><Icon type="book" />Methodology</button>
                    </div>
                </section>
                {errorDisplayStatus && <Alert variant="danger">{error}</Alert>}
                {loading && <div className="index-v3-loading"><Spinner animation="border" role="status" /><span>Building dashboard…</span></div>}
                {!loading && hasData && <>
                    <section className="index-v3-grid index-v3-grid-3">
                        <StatCard icon="companies" label="Companies" value={companyCount.toLocaleString('en-US')} detail={`${indexLabel}${sector === 'All Sectors' ? '' : ` · ${sector}`}`} />
                        <StatCard icon="transition" label={`${roleCode} Transitions`} value={transitionCount.toLocaleString('en-US')} detail={companyCount ? `${formatPercent(transitionCount / companyCount)} of companies` : 'N/A'} />
                        <StatCard icon="award" label="Companies with Special Awards" value={specialCompanyCount.toLocaleString('en-US')} detail={companyCount ? `${formatPercent(specialCompanyCount / companyCount)} of companies` : 'N/A'} />
                    </section>
                    <section className="index-v3-section">
                        <div className="index-v3-pay-layout">
                            <div className="index-v3-pay-level-column">
                                <div className="index-v3-section-heading"><div><span>MARKET SNAPSHOT</span><h2>Executive Pay Levels</h2></div></div>
                                <div className="index-v3-pay-levels"><table><thead><tr><th aria-label="Percentile" />{payLevels.map((level) => <th key={level.label}>{level.label}</th>)}</tr></thead><tbody>{[['P25', 'p25'], ['Median', 'median'], ['P75', 'p75']].map(([label, key]) => <tr key={key}><th>{label}</th>{payLevels.map((level) => <td key={level.label}>{formatCurrency(level[key])}</td>)}</tr>)}</tbody></table></div>
                            </div>
                            <div className="index-v3-pay-mix"><div><div className="index-v3-eyebrow">Median Pay Mix</div><h3>Compensation composition</h3></div><PayMixDonut payMix={payMix} /></div>
                        </div>
                    </section>
                    <section className="index-v3-section">
                        <div className="index-v3-analyze"><div><span>ANALYZE</span><h2>Select a compensation element</h2></div><div className="index-v3-segmented">{ELEMENTS.map((value) => <button type="button" className={element === value ? 'active' : ''} onClick={() => setElement(value)} key={value}>{value}</button>)}</div></div>
                        <div className="index-v3-grid index-v3-grid-3 index-v3-dynamic-kpis">
                            <StatCard label={`Median ${element}`} value={formatCurrency(elementStats.median)} detail={`N=${elementStats.count ?? 0}`} />
                            <StatCard label="Median YoY Change" value={<ChangeValue percent={elementStats.medianYoy} dollars={elementStats.medianDollarChange} />} detail={`N=${elementStats.yoyCount ?? 0}`} />
                            <StatCard label="Incumbent Median YoY" value={<ChangeValue percent={elementStats.incumbentMedianYoy} dollars={elementStats.incumbentMedianDollarChange} />} detail={`N=${elementStats.incumbentYoyCount ?? 0}`} />
                        </div>
                    </section>
                    <section className="index-v3-section index-v3-chart-section">
                        <div className="index-v3-section-heading"><div><span>DISTRIBUTION</span><h2>{element} Market Range</h2></div></div>
                        <DistributionChart distribution={elementStats.distribution} />
                    </section>
                    <section className="index-v3-section index-v3-chart-section">
                        <div className="index-v3-section-heading"><div><span>THREE-YEAR TREND</span><h2>Median {element} Three-Year Trend</h2></div></div>
                        <TrendChart points={trend} />
                    </section>
                    <section className="index-v3-section index-v3-transition-section">
                        <div className="index-v3-section-heading"><div><span>LEADERSHIP CHANGE</span><h2>{roleCode} Transition Analysis</h2></div><button type="button" className="index-v3-expand" onClick={() => setShowAwardTypes((value) => !value)} aria-expanded={showAwardTypes}>{showAwardTypes ? 'Hide' : 'Show'} special award detail <span>{showAwardTypes ? '−' : '+'}</span></button></div>
                        <div className="index-v3-table-wrap"><table><thead><tr><th>Metric</th>{transitions?.map((group) => <th key={group.transitionType}>{group.transitionType}<small>{group.companyCount} companies</small></th>)}</tr></thead><tbody><tr><td>Median {element}</td>{transitions?.map((group) => <td key={group.transitionType}>{formatCurrency(group.median)}</td>)}</tr><tr><td>Median YoY Change</td>{transitions?.map((group) => <td key={group.transitionType}>{formatPercent(group.medianYoy)}</td>)}</tr><tr><td>Companies with Special Awards</td>{transitions.map((group) => <td key={group.transitionType}>{group.specialAwardCount} <small>({formatPercent(group.specialAwardPercent)})</small></td>)}</tr>{showAwardTypes && Array.from(new Set(transitions.flatMap((group) => group.awardTypes?.map((award) => award.grantType)))).sort()?.map((grantType) => <tr className="index-v3-award-row" key={grantType}><td>{grantType}</td>{transitions?.map((group) => { const award = group.awardTypes?.find((item) => item.grantType === grantType); return <td key={group.transitionType}>{award ? `${award.companyCount} (${formatPercent(award.percent)})` : '0 (0.0%)'}</td>; })}</tr>)}</tbody></table></div>
                        <p className="index-v3-note">Note: For both Internal Promotion and External Hire, YoY compensation change compares the company's latest {roleCode} compensation with that same company's prior-year {roleCode} compensation.</p>
                        <p className="index-v3-note">Award-type percentages use distinct companies within each transition group. A company may receive more than one award type, so percentages may sum to more than 100%.</p>
                    </section>
                </>}
                {!loading && !errorDisplayStatus && !hasData && <div className="index-v3-empty-page">No dashboard data are available for this selection.</div>}
            </main>
        </div>
    );
};

export default Top5DashboardIndexV3View;
